import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SignatureCanvas from "react-signature-canvas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PenLine, RotateCcw, Check, Loader2 } from "lucide-react";
import { apiGetMySignature, apiSaveMySignature } from "@/lib/api";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSign: (signaturePng: string) => Promise<void>;
}

export function SignatureModal({ open, onOpenChange, onSign }: Props) {
  const { t } = useTranslation();
  const sigPadRef = useRef<SignatureCanvas>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [loading, setLoading] = useState(false);
  const [storedSignature, setStoredSignature] = useState<string | null>(null);
  const [useStored, setUseStored] = useState(false);
  const [loadingStoredSig, setLoadingStoredSig] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingStoredSig(true);
    apiGetMySignature()
      .then(({ signaturePng }) => {
        setStoredSignature(signaturePng);
        if (signaturePng) setUseStored(true);
      })
      .catch(() => {
        // Best-effort: if loading the saved signature fails, keep draw mode available.
      })
      .finally(() => setLoadingStoredSig(false));
  }, [open]);

  const handleClear = () => {
    sigPadRef.current?.clear();
    setIsEmpty(true);
  };

  const handleBegin = () => {
    setIsEmpty(false);
  };

  const handleSubmit = async () => {
    if (useStored && storedSignature) {
      setLoading(true);
      try {
        await onSign(storedSignature);
        onOpenChange(false);
      } catch {
        // The parent displays the signing error; keep the modal open for retry.
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!sigPadRef.current || sigPadRef.current.isEmpty()) return;

    const pngDataUrl = sigPadRef.current.getCanvas().toDataURL("image/png");

    setLoading(true);
    try {
      apiSaveMySignature(pngDataUrl).catch(() => {});
      await onSign(pngDataUrl);
      handleClear();
      onOpenChange(false);
    } catch {
      // The parent displays the signing error; keep the drawing available for retry.
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (value: boolean) => {
    if (!loading) {
      if (!value) {
        handleClear();
        setUseStored(false);
        setStoredSignature(null);
      }
      onOpenChange(value);
    }
  };

  const canSubmit = useStored ? Boolean(storedSignature) : !isEmpty;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px] p-0 gap-0 overflow-hidden">
        <div className="px-6 pt-6 pb-4 bg-muted/30 border-b">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <PenLine className="h-4 w-4 text-primary" />
              </div>
              {t("documents.signingTitle")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mt-2">
            {storedSignature
              ? t("documents.signature.savedDescription")
              : t("documents.signingDesc")}
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          {loadingStoredSig ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {storedSignature && (
                <div className="flex rounded-lg border overflow-hidden">
                  <button
                    type="button"
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      useStored
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-muted-foreground hover:bg-muted"
                    }`}
                    onClick={() => setUseStored(true)}
                    disabled={loading}
                  >
                    {t("documents.signature.useSaved")}
                  </button>
                  <button
                    type="button"
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      !useStored
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-muted-foreground hover:bg-muted"
                    }`}
                    onClick={() => {
                      setUseStored(false);
                      setIsEmpty(true);
                      setTimeout(() => sigPadRef.current?.clear(), 0);
                    }}
                    disabled={loading}
                  >
                    {t("documents.signature.drawNew")}
                  </button>
                </div>
              )}

              {useStored && storedSignature && (
                <div className="rounded-xl border-2 border-dashed border-border bg-muted/20 p-4 flex items-center justify-center min-h-[160px]">
                  <img
                    src={storedSignature}
                    alt={t("documents.signature.previewAlt")}
                    className="max-h-[120px] max-w-full object-contain"
                  />
                </div>
              )}

              {!useStored && (
                <div className="relative rounded-xl border-2 border-dashed border-border bg-muted/20 overflow-hidden">
                  <SignatureCanvas
                    ref={sigPadRef}
                    penColor="hsl(263, 70%, 48%)"
                    onBegin={handleBegin}
                    canvasProps={{
                      className: "w-full",
                      style: { height: 160, display: "block" },
                    }}
                  />
                  {isEmpty && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <span className="text-sm text-muted-foreground italic">
                        {t("documents.signingPlaceholder")}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {!useStored && (
                <p className="text-xs text-muted-foreground">
                  {t("documents.signingHint")}
                </p>
              )}
            </>
          )}
        </div>

        <div className="px-6 pb-6 pt-1 border-t bg-muted/20 flex items-center justify-between gap-3">
          {!useStored ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClear}
              disabled={loading || isEmpty}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              {t("documents.clearSig")}
            </Button>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !canSubmit || loadingStoredSig}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 border-0"
            >
              {loading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  {t("documents.signing")}
                </>
              ) : (
                <>
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                  {t("documents.applySig")}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
